import React, { useState, useEffect, useCallback } from "react";
import {
  Trash2,
  User,
  Mail,
  Calendar,
  Shield,
  Loader2,
  RefreshCw,
  Database,
} from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Pagination } from "../../components/ui/Pagination";
import { Card, CardContent } from "../../components/ui/Card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/Table";
import { Badge } from "../../components/ui/Badge";
import { SearchFilterForm } from "../../components/ui/SearchFilterForm";
import { Modal, ModalContent, ModalFooter } from "../../components/ui/Modal";
import { Select } from "../../components/ui/Select";
import { usersApi } from "../../utils/api";

const roleOptions = [
  { value: "user", label: "User" },
  { value: "admin", label: "Administrator" },
];

const PAGE_SIZE = 100;

const getRoleBadge = (role) => {
  const variants = {
    admin: "default",
    moderator: "warning",
    user: "outline",
  };
  const labels = {
    admin: "Admin",
    moderator: "Moderator",
    user: "User",
  };
  return <Badge variant={variants[role] || "outline"}>{labels[role] || role}</Badge>;
};

const getUserDisplayName = (user) => user.display_name || "No name";

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [selectedUser, setSelectedUser] = useState(null);

  // Role modal
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [newRole, setNewRole] = useState("user");
  const [isUpdatingRole, setIsUpdatingRole] = useState(false);

  // Delete modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Pagination
  const [offset, setOffset] = useState(0);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await usersApi.getAll({
        limit: PAGE_SIZE,
        offset,
      });
      setUsers(data.users || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error("Error fetching users:", err);
      setError(err.response?.data?.detail || "Error loading users");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [offset]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(null), 4000);
    return () => clearTimeout(t);
  }, [error]);

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(null), 4000);
    return () => clearTimeout(t);
  }, [success]);

  // Client-side search filter (API doesn't support search param)
  const searchLower = searchTerm.trim().toLowerCase();
  const filteredUsers = users.filter(
    (u) =>
      !searchLower ||
      [u.display_name, u.email, u.id].some((f) =>
        (f || "").toLowerCase().includes(searchLower)
      )
  );

  const adminsOnPage = users.filter((u) => u.role === "admin").length;

  const handleOpenRoleModal = (user) => {
    setSelectedUser(user);
    setNewRole(user.role || "user");
    setIsRoleModalOpen(true);
  };

  const handleSetRole = async () => {
    if (!selectedUser) return;
    setIsUpdatingRole(true);
    try {
      await usersApi.setRole(selectedUser.id, newRole);
      setSuccess(`Role updated to "${newRole}" for ${getUserDisplayName(selectedUser)}`);
      setIsRoleModalOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (err) {
      console.error("Error setting role:", err);
      setError(
        err.response?.data?.detail || "Error changing user role"
      );
    } finally {
      setIsUpdatingRole(false);
    }
  };

  const handleOpenDeleteModal = (user) => {
    setSelectedUser(user);
    setIsDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedUser) return;
    setIsDeleting(true);
    try {
      const deletedName = getUserDisplayName(selectedUser);
      await usersApi.delete(selectedUser.id);
      setSuccess(`User "${deletedName}" deleted successfully`);
      setIsDeleteModalOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (err) {
      console.error("Error deleting user:", err);
      setError(
        err.response?.data?.detail || "Error deleting user"
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const goToPage = (page) => {
    setOffset((page - 1) * PAGE_SIZE);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Users</h2>
          <p className="text-muted-foreground">
            Manage platform users
          </p>
        </div>
        <Button variant="outline" onClick={fetchUsers} disabled={loading}>
          {loading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          Refresh
        </Button>
      </div>

      {success && (
        <div className="p-3 rounded-md text-sm bg-green-500/10 text-green-700 border border-green-500/20">
          {success}
          <button className="ml-2 underline" onClick={() => setSuccess(null)}>Dismiss</button>
        </div>
      )}
      {error && (
        <div className="p-3 rounded-md text-sm bg-red-500/10 text-red-700 border border-red-500/20">
          {error}
          <button className="ml-2 underline" onClick={() => setError(null)}>
            Dismiss
          </button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid gap-4 sm:grid-cols-2">
        <Card>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <User className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{total}</p>
                <p className="text-sm text-muted-foreground">Total Users</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-warning/10 flex items-center justify-center">
                <Shield className="h-6 w-6 text-warning" />
              </div>
              <div>
                <p className="text-2xl font-bold">{adminsOnPage}</p>
                <p className="text-sm text-muted-foreground">Admins (this page)</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardContent>
          <SearchFilterForm
            value={searchTerm}
            onChange={setSearchTerm}
            onSubmit={(e) => e.preventDefault()}
            onClear={() => setSearchTerm("")}
            placeholder="Search users by name, email or ID"
            inputClassName="sm:w-64"
          />
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">
                Loading users...
              </span>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Database className="h-12 w-12 mb-4" />
              <p className="text-lg font-medium">
                No users found
              </p>
            </div>
          ) : (
            <>
              <div className="divide-y divide-border lg:hidden">
                {filteredUsers.map((user) => (
                  <div key={user.id} className="space-y-3 p-4">
                    <div className="flex items-start gap-3">
                      <div className="h-11 w-11 shrink-0 overflow-hidden rounded-full bg-primary/10 flex items-center justify-center">
                        {user.picture ? (
                          <img
                            src={user.picture}
                            alt={getUserDisplayName(user)}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <User className="h-5 w-5 text-primary" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium">{getUserDisplayName(user)}</p>
                        <p className="break-all text-sm text-muted-foreground">{user.email || "-"}</p>
                        {user.country && (
                          <p className="text-xs text-muted-foreground">{user.country}</p>
                        )}
                      </div>
                      {getRoleBadge(user.role)}
                    </div>

                    <div className="flex flex-wrap gap-1">
                      {(user.providers || []).map((provider) => (
                        <Badge key={provider} variant="outline" className="text-xs">
                          {provider}
                        </Badge>
                      ))}
                      {(user.providers || []).length === 0 && (
                        <span className="text-xs text-muted-foreground">No providers</span>
                      )}
                    </div>

                    <div className="flex flex-col gap-3 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {user.created_at
                          ? new Date(user.created_at).toLocaleDateString("en-US")
                          : "-"}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenRoleModal(user)}
                          title="Change role"
                        >
                          <Shield className="mr-2 h-4 w-4" />
                          Role
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDeleteModal(user)}
                          title="Delete user"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="hidden lg:block">
                <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Providers</TableHead>
                  <TableHead>Registered</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                          {user.picture ? (
                            <img
                              src={user.picture}
                              alt={getUserDisplayName(user)}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <User className="h-5 w-5 text-primary" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">
                            {getUserDisplayName(user)}
                          </p>
                          {user.country && (
                            <p className="text-xs text-muted-foreground">
                              {user.country}
                            </p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{user.email || "-"}</span>
                      </div>
                    </TableCell>
                    <TableCell>{getRoleBadge(user.role)}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(user.providers || []).map((provider) => (
                          <Badge
                            key={provider}
                            variant="outline"
                            className="text-xs"
                          >
                            {provider}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        {user.created_at
                          ? new Date(user.created_at).toLocaleDateString(
                              "en-US"
                            )
                          : "-"}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenRoleModal(user)}
                          title="Change role"
                        >
                          <Shield className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDeleteModal(user)}
                          title="Delete user"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <Pagination page={currentPage} totalPages={totalPages} onPageChange={goToPage} />

      {/* Role Change Modal */}
      <Modal open={isRoleModalOpen} onOpenChange={setIsRoleModalOpen}>
        <ModalContent title="Change User Role">
          {selectedUser && (
            <div className="space-y-4">
              <p className="text-muted-foreground">
                Change the role of{" "}
                <strong>{getUserDisplayName(selectedUser)}</strong> (
                {selectedUser.email})
              </p>
              <Select
                label="New Role"
                name="role"
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                options={roleOptions}
              />
              <ModalFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsRoleModalOpen(false);
                    setSelectedUser(null);
                  }}
                  disabled={isUpdatingRole}
                >
                  Cancel
                </Button>
                <Button onClick={handleSetRole} disabled={isUpdatingRole}>
                  {isUpdatingRole ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    "Save Role"
                  )}
                </Button>
              </ModalFooter>
            </div>
          )}
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <ModalContent title="Delete User">
          <p className="text-muted-foreground">
            Are you sure you want to delete the user "
            {selectedUser
              ? getUserDisplayName(selectedUser)
              : ""
            }"? This action cannot be undone.
          </p>
          <ModalFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setSelectedUser(null);
              }}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
}
